/**
 * Utility functions for polygon calculations and merging
 */

/**
 * Generate color based on day number for variety
 * @param {number} dayNumber - Day number (1-based)
 * @returns {string} Hex color code
 */
export function getPolygonColor(dayNumber) {
  const colors = [
    '#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8',
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6c757d',
    '#0056b3', '#1e7e34', '#856404', '#bd2130', '#117a8b'
  ];
  const colorIndex = (dayNumber - 1) % colors.length;
  return colors[colorIndex];
}

/**
 * Calculate z-index for polygon (lower day numbers have higher z-index to appear on top)
 * @param {number} dayNumber - Day number
 * @param {number} baseZIndex - Base z-index value (default: 1000)
 * @returns {number} Calculated z-index
 */
export function calculatePolygonZIndex(dayNumber, baseZIndex = 1000) {
  return baseZIndex - dayNumber;
}

/**
 * Format day numbers/ranges for label display
 * Consecutive days are shown as ranges (e.g., "1-2"), non-consecutive as comma-separated (e.g., "1,15")
 * @param {number|null} dayNumber - Single day number
 * @param {Array|null} days - Array of day numbers
 * @returns {string} Formatted label text
 */
export function formatDayLabel(dayNumber, days = null) {
  if (days && days.length > 1) {
    const sortedDays = [...days].sort((a, b) => a - b);
    
    // Group consecutive days
    const groups = [];
    let currentGroup = [sortedDays[0]];
    
    for (let i = 1; i < sortedDays.length; i++) {
      if (sortedDays[i] === sortedDays[i - 1] + 1) {
        // Consecutive - add to current group
        currentGroup.push(sortedDays[i]);
      } else {
        // Not consecutive - save current group and start new one
        groups.push(currentGroup);
        currentGroup = [sortedDays[i]];
      }
    }
    groups.push(currentGroup); // Add last group
    
    // Format groups: consecutive groups as ranges, others as individual days
    const formattedGroups = groups.map(group => {
      if (group.length === 1) {
        return group[0].toString();
      } else {
        return `${group[0]}-${group[group.length - 1]}`;
      }
    });
    
    return formattedGroups.join(',');
  } else {
    // Single day
    return (dayNumber || (days && days[0]) || 1).toString();
  }
}

/**
 * Create rectangle (bounding box) that includes all points
 * Ensures minimum size is 5% of map width
 * @param {Array} points - Array of {lat, lng} objects
 * @param {Object} appMap - Google Maps instance (optional, for calculating minimum size)
 * @returns {Array} Array of 4 points forming a rectangle
 */
export function createBoundingBoxFromPoints(points, appMap = null) {
  if (points.length === 0) {
    return [];
  }
  
  // Find min/max latitude and longitude
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLng = points[0].lng;
  let maxLng = points[0].lng;
  
  points.forEach(point => {
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  });
  
  // Calculate current rectangle dimensions
  const currentWidth = maxLng - minLng;
  const currentHeight = maxLat - minLat;
  
  // Get map bounds to calculate map width
  let minWidth = 0;
  let minHeight = 0;
  
  if (appMap) {
    try {
      const bounds = appMap.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const mapWidth = ne.lng() - sw.lng();
        const mapHeight = ne.lat() - sw.lat();
        
        // Minimum size is 5% of map width/height
        minWidth = mapWidth * 0.05;
        minHeight = mapHeight * 0.05;
      }
    } catch (e) {
      // If we can't get bounds, use a default minimum
      minWidth = 0.01; // ~1km at equator
      minHeight = 0.01;
    }
  } else {
    // Fallback if map is not available
    minWidth = 0.01;
    minHeight = 0.01;
  }
  
  // Expand rectangle if it's smaller than minimum
  if (currentWidth < minWidth) {
    const centerLng = (minLng + maxLng) / 2;
    minLng = centerLng - minWidth / 2;
    maxLng = centerLng + minWidth / 2;
  }
  
  if (currentHeight < minHeight) {
    const centerLat = (minLat + maxLat) / 2;
    minLat = centerLat - minHeight / 2;
    maxLat = centerLat + minHeight / 2;
  }
  
  // Create rectangle with sides parallel to map edges
  // Order: top-left, top-right, bottom-right, bottom-left
  return [
    { lat: maxLat, lng: minLng }, // Top-left (north-west)
    { lat: maxLat, lng: maxLng }, // Top-right (north-east)
    { lat: minLat, lng: maxLng }, // Bottom-right (south-east)
    { lat: minLat, lng: minLng }  // Bottom-left (south-west)
  ];
}

/**
 * Calculate area of a rectangle polygon
 * @param {Array} polygonPath - Array of {lat, lng} points forming a rectangle
 * @returns {number} Area of the polygon
 */
export function calculatePolygonArea(polygonPath) {
  if (polygonPath.length < 4) return 0;
  
  // For rectangles, calculate width * height
  const minLat = Math.min(...polygonPath.map(p => p.lat));
  const maxLat = Math.max(...polygonPath.map(p => p.lat));
  const minLng = Math.min(...polygonPath.map(p => p.lng));
  const maxLng = Math.max(...polygonPath.map(p => p.lng));
  
  const width = maxLng - minLng;
  const height = maxLat - minLat;
  
  return width * height;
}

/**
 * Calculate overlapping area between two rectangle polygons
 * @param {Array} polygonPath1 - First polygon path
 * @param {Array} polygonPath2 - Second polygon path
 * @returns {number} Overlapping area
 */
export function calculateOverlap(polygonPath1, polygonPath2) {
  // Get bounds for each polygon
  const getBounds = (path) => {
    return {
      minLat: Math.min(...path.map(p => p.lat)),
      maxLat: Math.max(...path.map(p => p.lat)),
      minLng: Math.min(...path.map(p => p.lng)),
      maxLng: Math.max(...path.map(p => p.lng))
    };
  };
  
  const bounds1 = getBounds(polygonPath1);
  const bounds2 = getBounds(polygonPath2);
  
  // Calculate intersection
  const overlapMinLat = Math.max(bounds1.minLat, bounds2.minLat);
  const overlapMaxLat = Math.min(bounds1.maxLat, bounds2.maxLat);
  const overlapMinLng = Math.max(bounds1.minLng, bounds2.minLng);
  const overlapMaxLng = Math.min(bounds1.maxLng, bounds2.maxLng);
  
  // Check if there's an overlap
  if (overlapMinLat >= overlapMaxLat || overlapMinLng >= overlapMaxLng) {
    return 0;
  }
  
  // Calculate overlap area
  const overlapWidth = overlapMaxLng - overlapMinLng;
  const overlapHeight = overlapMaxLat - overlapMinLat;
  return overlapWidth * overlapHeight;
}

/**
 * Check if two polygons should be merged (overlap > 60% of both)
 * @param {Array} polygonPath1 - First polygon path
 * @param {Array} polygonPath2 - Second polygon path
 * @returns {boolean} True if polygons should be merged
 */
export function shouldMergePolygons(polygonPath1, polygonPath2) {
  const area1 = calculatePolygonArea(polygonPath1);
  const area2 = calculatePolygonArea(polygonPath2);
  const overlapArea = calculateOverlap(polygonPath1, polygonPath2);
  
  if (area1 === 0 || area2 === 0) return false;
  
  const overlapPercent1 = (overlapArea / area1) * 100;
  const overlapPercent2 = (overlapArea / area2) * 100;
  
  // Merge if overlap is more than 60% of both polygons
  return overlapPercent1 > 60 && overlapPercent2 > 60;
}

/**
 * Merge two polygon paths into one bounding box
 * @param {Array} path1 - First polygon path
 * @param {Array} path2 - Second polygon path
 * @returns {Array} Merged polygon path
 */
export function mergePolygonPaths(path1, path2) {
  // Get all points from both paths
  const allPoints = [...path1, ...path2];
  
  // Find min/max for bounding box
  let minLat = allPoints[0].lat;
  let maxLat = allPoints[0].lat;
  let minLng = allPoints[0].lng;
  let maxLng = allPoints[0].lng;
  
  allPoints.forEach(point => {
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  });
  
  // Create merged bounding box
  return [
    { lat: maxLat, lng: minLng }, // Top-left
    { lat: maxLat, lng: maxLng }, // Top-right
    { lat: minLat, lng: maxLng }, // Bottom-right
    { lat: minLat, lng: minLng }  // Bottom-left
  ];
}

/**
 * Merge overlapping polygons
 * @param {Array} polygons - Array of polygon objects with {polygonPath, dayNumber, days}
 * @returns {Array} Array of merged polygons
 */
export function mergeOverlappingPolygons(polygons) {
  if (polygons.length === 0) {
    return [];
  }
  
  const merged = [];
  const processed = new Set();
  
  polygons.forEach((polygon, index) => {
    if (processed.has(index)) {
      return;
    }
    
    let currentPolygon = { ...polygon };
    processed.add(index);
    
    // Find all overlapping polygons and merge them
    let foundOverlap = true;
    while (foundOverlap) {
      foundOverlap = false;
      
      polygons.forEach((otherPolygon, otherIndex) => {
        if (processed.has(otherIndex)) {
          return;
        }
        
        if (shouldMergePolygons(currentPolygon.polygonPath, otherPolygon.polygonPath)) {
          // Merge the polygons
          const mergedPath = mergePolygonPaths(currentPolygon.polygonPath, otherPolygon.polygonPath);
          const mergedDays = [...currentPolygon.days, ...otherPolygon.days].sort((a, b) => a - b);
          
          currentPolygon = {
            polygonPath: mergedPath,
            dayNumber: mergedDays[0], // Use first day for color
            days: mergedDays
          };
          
          processed.add(otherIndex);
          foundOverlap = true;
        }
      });
    }
    
    merged.push(currentPolygon);
  });
  
  return merged;
}

